/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import {
  ActionType,
  ActionSortFields,
  ActionScope,
  PlanSortFields,
  SortOrder,
} from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { customActionsExcludeFilterMap } from "~/tools/filterStateMaps";
import { CustomActionQuery } from "~/views/customActions/types";
import { useGetPlanNames } from "~/views/plans/hooks/useGetPlanNames/useGetPlanNames";
import { useGetCustomActions } from "../hooks/useGetCustomActions/useGetCustomActions";
import { prepareCustomActionsFilter } from "./customActionFilterBuilders";

type State = {
  startDate?: string;
  endDate?: string;
  types: ActionType[];
  excludeTypes: boolean;
  scopes: ActionScope[];
  excludeScopes: boolean;
  planIds: string[];
  excludePlanIds: boolean;
};
type CustomActionQueryState = CustomActionQuery & {
  page?: string | number;
  limit?: string | number;
};

const actionTypeCheckboxes = Object.values(ActionType).map((type) => ({
  value: type,
  label: type,
}));

const actionScopeCheckboxes = Object.values(ActionScope).map((scope) => ({
  value: scope,
  label: scope,
}));

function buildState(queryState: CustomActionQuery) {
  return {
    startDate: queryState.startDateUTC
      ? dayjs(queryState.startDateUTC).format(dateFormat.shortDate)
      : undefined,
    endDate: queryState.endDateUTC
      ? dayjs(queryState.endDateUTC).format(dateFormat.shortDate)
      : undefined,
    types: queryState.types ?? [],
    excludeTypes: queryState.excludeTypes ?? false,
    scopes: queryState.scopes ?? [],
    excludeScopes: queryState.excludeScopes ?? false,
    planIds: queryState.planIds ?? [],
    excludePlanIds: queryState.excludePlanIds ?? false,
  };
}

function buildQueryStateFromSidebarState(
  queryState: CustomActionQueryState,
  state: State
): CustomActionQueryState {
  return {
    ...queryState,
    startDateUTC: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    endDateUTC: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
    types: state.types,
    excludeTypes: state.excludeTypes,
    scopes: state.scopes,
    excludeScopes: state.excludeScopes,
    planIds: state.planIds,
    excludePlanIds: state.excludePlanIds,
  };
}

export function ActionFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<CustomActionQuery>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>): State => ({
      ...data,
      ...partialData,
    }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    customActionsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const { count: previewCount, loading: previewLoading } = useGetCustomActions({
    page: String(draftQueryState.page ?? "1"),
    limit: String(draftQueryState.limit ?? "20"),
    sort: {
      field: draftQueryState.field ?? ActionSortFields.Name,
      order: draftQueryState.order ?? SortOrder.Asc,
    },
    filter: prepareCustomActionsFilter(draftQueryState),
    skip: !isOpen || !hasPendingChanges,
  });
  const previewResultsCount = previewLoading ? undefined : previewCount;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "actions",
  });

  const { data: plans } = useGetPlanNames({
    page: "1",
    limit: "1000",
    sort: {
      field: PlanSortFields.Name,
      order: SortOrder.Asc,
    },
  });

  const planCheckboxes = (plans ?? []).map((plan) => ({
    value: plan.id,
    label: plan.name,
  }));

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);
    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      types: [],
      excludeTypes: false,
      scopes: [],
      excludeScopes: false,
      planIds: [],
      excludePlanIds: false,
    });
    updateQueryState({
      ...queryState,
      startDateUTC: undefined,
      endDateUTC: undefined,
      types: [],
      excludeTypes: false,
      scopes: [],
      excludeScopes: false,
      planIds: [],
      excludePlanIds: false,
      page: "1",
    });
    close();
  };

  const clearDateCreatedFilter = () => {
    updateState({ startDate: undefined, endDate: undefined });
  };

  const clearTypesFilter = () => {
    updateState({
      types: [],
      excludeTypes: false,
    });
  };

  const clearScopesFilter = () => {
    updateState({
      scopes: [],
      excludeScopes: false,
    });
  };

  const clearPlansFilter = () => {
    updateState({
      planIds: [],
      excludePlanIds: false,
    });
  };
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "actions",
    excludeFilterMap: customActionsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Creation Date"
            isSelected={!!state?.startDate && !!state?.endDate}
            selectedOptions={["startDate", "endDate"]}
            onClear={clearDateCreatedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={(range) => updateState(range)}
              label="Time range when action was created"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterWrapper
            name="Type"
            isSelected={isNotNil(state.types) && isNotEmpty(state.types)}
            selectedOptions={state.types}
            onClear={clearTypesFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.types}
              selectableItems={actionTypeCheckboxes}
              isExcludeSelected={state.excludeTypes}
              onMultiSelect={(types) =>
                updateState({ types: types as ActionType[] })
              }
              onModeChange={(mode) =>
                updateState({ excludeTypes: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Scope"
            isSelected={isNotNil(state.scopes) && isNotEmpty(state.scopes)}
            selectedOptions={state.scopes}
            onClear={clearScopesFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.scopes}
              selectableItems={actionScopeCheckboxes}
              isExcludeSelected={state.excludeScopes}
              onMultiSelect={(scopes) =>
                updateState({ scopes: scopes as ActionScope[] })
              }
              onModeChange={(mode) =>
                updateState({ excludeScopes: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Related Plan"
            isSelected={isNotNil(state.planIds) && isNotEmpty(state.planIds)}
            selectedOptions={state.planIds}
            onClear={clearPlansFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.planIds}
              selectableItems={planCheckboxes}
              isExcludeSelected={state.excludePlanIds}
              onMultiSelect={(planIds) =>
                updateState({ planIds: planIds as string[] })
              }
              onModeChange={(mode) =>
                updateState({ excludePlanIds: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
